/**
 * Studio depot / VCS contracts shared by studio-api and the Electron client.
 * Path layout and ACL semantics match legacy studio-api (additive compatibility).
 */
import { z } from "zod";
import { STUDIO_ALLOWED_ROLES, type StudioAllowedRole } from "./roles";

export const studioRoleSchema = z.enum(STUDIO_ALLOWED_ROLES);
export type StudioRole = StudioAllowedRole;

export const studioDepotSectionSchema = z.enum([
  "development",
  "production",
  "private",
  "shared",
  "other",
]);
export type StudioDepotSection = z.infer<typeof studioDepotSectionSchema>;

export const studioPathPermissionSchema = z.enum(["read", "write"]);
export type StudioPathPermission = z.infer<typeof studioPathPermissionSchema>;

export const studioFileKindSchema = z.enum(["blend", "glb", "txt", "other"]);
export type StudioFileKind = z.infer<typeof studioFileKindSchema>;

export const studioChangelistActionSchema = z.enum(["add", "edit", "delete"]);
export type StudioChangelistAction = z.infer<typeof studioChangelistActionSchema>;

export const studioSha256Schema = z
  .string()
  .regex(/^[a-fA-F0-9]{64}$/, "Invalid SHA-256");

export const createStudioWorkspaceSchema = z.object({
  machineFingerprint: z.string().min(1).max(200),
  root: z.string().min(1).max(1024),
  name: z.string().min(1).max(200),
});
export type CreateStudioWorkspaceInput = z.infer<
  typeof createStudioWorkspaceSchema
>;

export const studioLockBodySchema = z.object({
  path: z.string().min(1),
  workspaceId: z.string().min(1),
  machineFingerprint: z.string().max(200).optional(),
  reason: z.string().max(500).optional(),
});
export type StudioLockBody = z.infer<typeof studioLockBodySchema>;

export const studioChangelistFileSchema = z.object({
  path: z.string().min(1),
  action: studioChangelistActionSchema,
  blobSha256: studioSha256Schema.optional(),
  kind: studioFileKindSchema.optional(),
});

export const createStudioChangelistSchema = z.object({
  message: z.string().max(2000).default(""),
  files: z.array(studioChangelistFileSchema).min(1),
});
export type CreateStudioChangelistInput = z.infer<
  typeof createStudioChangelistSchema
>;

export const submitStudioChangelistSchema = z.object({
  keepLock: z.boolean().optional(),
});

export const initiateStudioUploadSchema = z.object({
  sha256: studioSha256Schema,
  sizeBytes: z.number().int().positive(),
  contentType: z.string().min(1).max(200).optional(),
});

/** Small-file single PUT ceiling (matches multipart part size). */
export const STUDIO_SINGLE_PUT_MAX_BYTES = 5 * 1024 * 1024;

export const studioBranchSlugSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/, "Invalid branch slug");

export const createStudioBranchSchema = z.object({
  slug: studioBranchSlugSchema,
  name: z.string().min(1).max(120).optional(),
  sourceBranchId: z.string().min(1).max(64).optional(),
});
export type CreateStudioBranchInput = z.infer<typeof createStudioBranchSchema>;

export const switchStudioBranchSchema = z.object({
  workspaceId: z.string().min(1),
  branchId: z.string().min(1).max(64),
});

export const studioPublishConflictResolutionSchema = z.object({
  relativePath: z.string().min(1),
  resolution: z.enum(["take_work", "keep_source"]),
});

export const publishStudioBranchSchema = z.object({
  conflictResolutions: z
    .array(studioPublishConflictResolutionSchema)
    .max(500)
    .optional(),
});

export const STUDIO_WORK_PREFIX = "branches/work";

export const createStudioShareSchema = z.object({
  path: z.string().min(1),
  granteeAccountId: z.string().min(1).max(200),
  permission: studioPathPermissionSchema.default("read"),
});

export const studioActorSchema = z.object({
  accountId: z.string().min(1),
  role: studioRoleSchema,
  sessionId: z.string().min(1),
  email: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  username: z.string().optional(),
  clientId: z.string().optional(),
  scope: z.string().optional(),
});
export type StudioActorDto = z.infer<typeof studioActorSchema>;

/** Worker→Worker OIDC introspect body (HMAC-signed separately). */
export const studioOidcIntrospectRequestSchema = z.object({
  accessToken: z.string().min(1),
});

export const STUDIO_DEVELOPMENT_PREFIX = "branches/development";
export const STUDIO_PRODUCTION_PREFIX = "branches/production";
export const STUDIO_PRIVATE_PREFIX = "private";
export const STUDIO_OAUTH_CLIENT_ID = "studio-desktop";
export const STUDIO_OAUTH_REDIRECT_URI = "vetty-studio://auth/callback";
/** Exact development loopback redirect (additive allowlist entry for studio-desktop). */
export const STUDIO_OAUTH_DEV_LOOPBACK_REDIRECT_URI =
  "http://127.0.0.1:53682/auth/callback";
export const STUDIO_LOCK_LEASE_MS = 15 * 60 * 1000;
